// -----------------------
// ToolkitDetail.js
// Helprr Unicorn Version 🦄
// With: progress, mark-as-done, psychoeducation, "why this helps",
//       confetti on 100%, theme modes (warm/calm/night)
// -----------------------

import React, { useState, useEffect, useMemo } from "react";
import OuterHeader from "../components/OuterHeader";
import Footer from "../components/Footer";
import { useParams, Link } from "react-router-dom";
import "../css2/Toolkit.css";
import RuralWellnessToolkit from "./toolkits/RuralWellnessToolkit";


// -----------------------------
// TOOLKIT DATA (ULTRA-PREMIUM CONTENT)
// -----------------------------


const TOOLKIT_DATA = {
  "emotional-first-aid": {
    emoji: "🌾",
    title: "Emotional First-Aid",
    intro:
      "Quick grounding tools to help you reset during moments of overwhelm, panic, or emotional intensity.",

    sections: [
      {
        name: "Understanding This Feeling",
        items: [
          {
            id: "efa-edu-core",
            type: "edu",
            title: "💡 Why Overwhelm Feels So Intense",
            body:
              "Overwhelm isn’t you “being dramatic.” It’s your nervous system hitting a point where it can’t hold everything at once, so it shifts into survival mode. Your heart rate speeds up, your thoughts scatter, and your body tries to protect you by narrowing your focus.",
            miniLessonTitle: "Mini Lesson: Why your brain spirals more at night",
            miniLessonBody:
              "At night, there are fewer distractions. Your emotional brain becomes louder and your logical brain powers down. Spirals feel heavier not because your problems are bigger, but because your support systems are quieter. You’re not weak — you’re human.",
            links: [
              "Try next: Capsule “It’s Okay to Feel Lost”",
              "Toolkit pairing: Daily Resilience Routine",
            ],
          },
        ],
      },

      {
        name: "Audio Support",
        items: [
          {
            id: "efa-audio-30-music",
            type: "audio",
            title: "30-Second Reset (With Music)",
            subtitle: "A gentle grounding moment when everything feels loud.",
            file: "/assets/audio/Audio 1 - M.m4a",
            why:
              "Short, guided audio gives your brain a clear, simple focus. This reduces mental noise and signals safety to your nervous system.",
          },
          {
            id: "efa-audio-30-voice",
            type: "audio",
            title: "30-Second Reset (No Music)",
            subtitle: "Plain, calm voice guidance for sensory-sensitive moments.",
            file: "/assets/audio/audio 1-Nm.m4a",
            why:
              "If you’re sensitive to sound, a voice-only track can reduce sensory overload while still guiding you into regulation.",
          },
          {
            id: "efa-audio-lost",
            type: "audio",
            title: "It's Okay to Feel Lost",
            subtitle: "Capsule reflection to soften self-judgment.",
            file: "/assets/audio/Capsule 1_ Its okay to feel lost.m4a",
            why:
              "Self-compassion lowers shame and calms the threat system in your brain. When you stop attacking yourself, your body can start to relax.",
          },
        ],
      },

      {
        name: "Grounding Steps",
        items: [
          {
            id: "efa-54321",
            type: "interactive",
            title: "5-4-3-2-1 Grounding",
            steps: [
              "Look around and name 5 things you can see right now.",
              "Gently touch 4 objects — notice texture, temperature, and weight.",
              "Listen for 3 different sounds — start with faraway, then closer.",
              "Notice 2 smells — or remember a scent you love.",
              "Name 1 thing you're grateful for in this moment.",
            ],
            why:
              "This technique pulls your senses into the present moment and signals safety to your brain. By engaging the senses, you interrupt spirals and anchor your nervous system.",
          },
          {
            id: "efa-name-feeling",
            type: "reflection",
            title: "Name the Feeling",
            prompts: [
              "Say out loud: “I feel ___ because ___.”",
              "If this feeling had a color, what would it be?",
              "Where in your body do you feel it the most?",
              "What do you need in this moment — comfort, space, movement, connection?",
            ],
            why:
              "Naming emotions reduces their intensity. Brain imaging shows that putting words to feelings calms the amygdala (your alarm system).",
          },
          {
            id: "efa-breathing-reset",
            type: "breathing",
            title: "Breathing Reset",
            description: "Follow the expanding circle: inhale for 4, exhale for 6.",
            file: "/assets/audio/Audio 2 -M.m4a",
            why:
              "Longer exhales activate the vagus nerve, which helps your heart rate slow and shifts your body out of fight-or-flight.",
          },
          {
            id: "efa-feet",
            type: "micro",
            title: "Feel Your Feet",
            description:
              "Press your feet into the floor gently. Notice the firmness and support underneath you.",
            icon: "👣",
            why:
              "Physical pressure and contact with the ground send a clear signal of safety and stability to your body.",
          },
          {
            id: "efa-chest",
            type: "micro",
            title: "Hand on Chest",
            description:
              "Place your hand on your chest… inhale for 4, exhale for 6. Notice the rise and fall.",
            icon: "✋",
            why:
              "Touch releases oxytocin and calms the stress response. Even your own hand can be registered as care.",
          },
          {
            id: "efa-cold",
            type: "micro",
            title: "Hold Something Cold",
            description:
              "Hold a cold object (glass of water, metal, ice pack) for a few seconds. Let the sensation bring you back into your body.",
            icon: "❄️",
            why:
              "Intense but safe sensations like cold redirect attention from spirals and help snap your nervous system out of overload.",
          },
        ],
      },
    ],
  },

  loneliness: {
    emoji: "💜",
    title: "Loneliness Toolkit",
    intro:
      "Gentle support for nights that feel too quiet, and days where you feel invisible.",

    sections: [
      {
        name: "Understanding This Feeling",
        items: [
          {
            id: "lonely-edu-core",
            type: "edu",
            title: "💡 Why Loneliness Hurts Like a Bruise",
            body:
              "Loneliness isn’t “needing attention.” Your brain treats social disconnection like physical danger — thousands of years ago, being alone meant not surviving. That’s why loneliness activates the same brain regions as physical pain.",
            miniLessonTitle: "Mini Lesson: Solitude vs. loneliness",
            miniLessonBody:
              "Solitude is chosen. Loneliness is isolation your body didn’t choose. One restores you, the other drains you. Naming the difference is an act of self-compassion.",
            links: [
              "Try next: Capsule “It’s Okay to Need People”",
              "Toolkit pairing: Daily Resilience Routine",
            ],
          },
        ],
      },

      {
        name: "Audio Companions",
        items: [
          {
            id: "lonely-invisible",
            type: "audio",
            title: "When You Feel Invisible",
            subtitle: "A soft reminder that you are not the problem.",
            file: "/assets/audio/Capsule 3_ When you feel invisible.m4a",
            why:
              "Hearing your experience reflected by someone else reduces shame and gives your nervous system the sense of “being seen.”",
          },
          {
            id: "lonely-need-people",
            type: "audio",
            title: "It's Okay to Need People",
            subtitle: "For when you’re tired of being ‘strong’ alone.",
            file: "/assets/audio/Capsule 4_ Its okay to need people.m4a",
            why:
              "Giving yourself permission to need others softens self-criticism, which is often what makes loneliness feel worse.",
          },
        ],
      },

      {
        name: "Connection Experiments",
        items: [
          {
            id: "lonely-five-word-text",
            type: "micro",
            title: "Send a 5-Word Check-In",
            description:
              'Text someone: “Thinking of you today 💛”. No explanation needed, no long conversation required.',
            icon: "📱",
            why:
              "Small, low-pressure bids for connection are easier to start and still give your nervous system a hit of belonging.",
          },
          {
            id: "lonely-public-space",
            type: "micro",
            title: "Be Around Humans",
            description:
              "Sit in a café, library, or shared space. You don’t have to talk — your body benefits from not being alone.",
            icon: "🏙️",
            why:
              "Your nervous system reads “other humans nearby” as safety, even if you’re not directly interacting.",
          },
          {
            id: "lonely-kindness",
            type: "micro",
            title: "Tiny Act of Kindness",
            description:
              "Hold the door, leave a kind comment, or compliment someone’s effort. Sometimes giving warmth helps us feel connected again.",
            icon: "🤝",
            why:
              "Acts of kindness activate reward circuits in the brain and gently remind you that you have impact.",
          },
        ],
      },

      {
        name: "Self-Soothing & Meaning",
        items: [
          {
            id: "lonely-reflection",
            type: "reflection",
            title: "What This Feeling Is (and Isn’t)",
            prompts: [
              "Loneliness often means: “I care about connection.” Where do you notice that care in your life?",
              "Which spaces or people feel even 1% safer than others?",
              "If you could design a small, kinder evening for yourself tonight, what would be in it?",
            ],
            why:
              "Reflecting on meaning shifts your brain from “I am the problem” to “this feeling is a signal.” That reduces shame and gives you options.",
          },
        ],
      },
    ],
  },

  "resilience-routine": {
    emoji: "🌤",
    title: "Daily Resilience Routine",
    intro:
      "Tiny, realistic practices you can repeat so your emotional muscles get stronger over time.",

    sections: [
      {
        name: "Understanding This Feeling",
        items: [
          {
            id: "res-edu-core",
            type: "edu",
            title: "💡 Why Tiny Habits Build Emotional Strength",
            body:
              "Your brain learns change through repetition, not force. Small predictable actions create tiny emotional wins that your nervous system can trust. Consistency beats intensity — especially when you’re tired or stressed.",
            miniLessonTitle: "Mini Lesson: Why change feels impossible when you’re stressed",
            miniLessonBody:
              "Stress shuts down the part of your brain responsible for planning and motivation. That’s why even small tasks feel impossible. The solution is not more discipline — it’s reducing intensity and adding gentle structure.",
            links: [
              "Try next: Capsule “Connection Starts Small”",
              "Toolkit pairing: Emotional First-Aid",
            ],
          },
        ],
      },

      {
        name: "Morning (2 Minutes)",
        items: [
          {
            id: "res-morning-check",
            type: "micro",
            title: "One-Sentence Check-In",
            description:
              'Ask: “How am I actually doing this morning?” Answer in one honest sentence.',
            icon: "🌅",
            why:
              "Honest self-awareness is the first step in regulation. Your brain can’t care for a state it refuses to acknowledge.",
          },
          {
            id: "res-morning-intention",
            type: "reflection",
            title: "Set a Gentle Intention",
            prompts: [
              "Today, I want to treat myself like someone who is: learning / tired / brave / healing.",
              "One thing I’ll allow myself to *not* be perfect at today is…",
            ],
            why:
              "Gentle intentions shift your focus from performance to care, which reduces anxiety and burnout.",
          },
        ],
      },

      {
        name: "Midday (1–3 Minutes)",
        items: [
          {
            id: "res-midday-body-scan",
            type: "micro",
            title: "Micro Body Scan",
            description:
              "Pause. Notice: jaw, shoulders, stomach, hands. Relax each area by 5%. No need to fix everything.",
            icon: "🧠",
            why:
              "Micro body scans help you catch tension early, before it snowballs into headaches, irritability, or shutdown.",
          },
          {
            id: "res-midday-breath",
            type: "breathing",
            title: "Box Breathing",
            description:
              "Inhale 4 • hold 4 • exhale 4 • hold 4. Repeat for 3–4 rounds.",
            file: "/assets/audio/Audio 3- M.m4a",
            why:
              "Box breathing balances your nervous system and stabilizes heart rate, which improves focus and emotional control.",
          },
        ],
      },

      {
        name: "Evening (3 Minutes)",
        items: [
          {
            id: "res-evening-wins",
            type: "reflection",
            title: "Three Tiny Wins",
            prompts: [
              "List 3 things you handled today (even if they feel small).",
              "What did today prove wrong about the harshest story in your head?",
            ],
            why:
              "Your brain has a negativity bias. Naming wins retrains your attention to notice evidence of capability.",
          },
          {
            id: "res-evening-audio",
            type: "audio",
            title: "Connection Starts Small",
            subtitle: "Wind-down audio about building resilience slowly.",
            file: "/assets/audio/Capsule 5_ Connection starts small 2.m4a",
            why:
              "Hearing that small steps count helps reduce all-or-nothing thinking, which is a major block to resilience.",
          },
        ],
      },
    ],
  },

  "winter-wellness": {
    emoji: "❄️",
    title: "Winter Wellness Toolkit",
    intro:
      "Support for seasonal mood dips, low light, and “what is sunlight” kind of days.",

    sections: [
      {
        name: "Understanding This Feeling",
        items: [
          {
            id: "winter-edu-core",
            type: "edu",
            title: "💡 Why Winter Affects Mood So Much",
            body:
              "Your brain is deeply light-sensitive. Low sunlight affects serotonin (mood), melatonin (sleep), and energy regulation. This is biology, not laziness.",
            miniLessonTitle: "Mini Lesson: Why winter feels like moving through molasses",
            miniLessonBody:
              "In winter, your circadian rhythm slows. Your internal clock can lag behind your day. Your thoughts might stay sharp, but your energy and motivation move slower. Your body isn’t failing — it’s adapting.",
            links: [
              "Try next: Emotional First-Aid on tough days",
              "Pair with: Daily Resilience Routine",
            ],
          },
        ],
      },

      {
        name: "Light & Rhythm",
        items: [
          {
            id: "winter-morning-light",
            type: "micro",
            title: "Morning Light Ritual",
            description:
              "Try to get outside within 60 minutes of waking, even for 3–5 minutes. Look at the sky (not the sun) to wake your system.",
            icon: "☀️",
            why:
              "Morning light anchors your circadian rhythm and helps regulate mood and sleep hormones.",
          },
          {
            id: "winter-stretch",
            type: "micro",
            title: "Warm-Up Stretch",
            description:
              "Do 5 slow stretches: neck, shoulders, arms, back, legs. Warmth plus movement tells your body you’re safe.",
            icon: "🧣",
            why:
              "Gentle movement increases blood flow and counters the physical heaviness that comes with low mood.",
          },
        ],
      },

      {
        name: "Mood & Energy",
        items: [
          {
            id: "winter-cozy-corner",
            type: "micro",
            title: "Cozy Corner",
            description:
              "Choose a chair/spot as your “winter comfort corner” with a blanket, warm drink, and something gentle to watch/read.",
            icon: "📚",
            why:
              "Predictable comfort spots lower decision fatigue and give your brain a reliable place to rest.",
          },
          {
            id: "winter-overwhelm-audio",
            type: "audio",
            title: "The Weight We Ignore",
            subtitle:
              "For days when your plate is full but you can’t name why you’re heavy.",
            file: "/assets/audio/Capsule 2_ The weight we ignore.m4a",
            why:
              "Naming the invisible stressors of winter (finances, social pressure, fatigue) helps your body release some of that silent load.",
          },
        ],
      },

      {
        name: "Connection & Structure",
        items: [
          {
            id: "winter-weekly-plan",
            type: "reflection",
            title: "Anchor Your Week",
            prompts: [
              "Pick 1 recurring thing that makes you feel human (call, walk, class). Put it in your calendar.",
              "Who is one “winter buddy” you can check in with once a week?",
            ],
            why:
              "Anchors give your week a sense of rhythm and predictability, which stabilizes mood during long winters.",
          },
        ],
      },
    ],
  },

  "rural-wellness": {
    emoji: "🚐",
    title: "Rural Wellness Toolkit",
    intro:
      "Tools designed with rural communities and on-the-go support in mind — in partnership with Wellness on Wheels.",

    sections: [
      {
        name: "Understanding This Feeling",
        items: [
          {
            id: "rural-edu-core",
            type: "edu",
            title: "💡 Why Rural Stress Feels Different",
            body:
              "Rural life often means more isolation, longer drives, fewer services, and less anonymity. It’s a unique emotional load — not “easier” or “harder” than city life, just different.",
            miniLessonTitle:
              "Mini Lesson: Why connection in rural areas looks different",
            miniLessonBody:
              "In cities, connection is optional. In rural communities, it’s survival — but often invisible. You don’t need a big social circle; you need one or two reliable people and a sense of belonging to your environment.",
            links: [
              "Try next: Loneliness Toolkit",
              "Partner spotlight: Wellness on Wheels",
            ],
          },
        ],
      },

      {
        name: "Low-Bandwidth Grounding",
        items: [
          {
            id: "rural-skyline",
            type: "micro",
            title: "Horizon Reset",
            description:
              "Stand outside and look at the horizon line for 30–60 seconds. Let your eyes rest on something far away.",
            icon: "🌾",
            why:
              "Looking at distant views relaxes eye muscles and signals the brain that you’re not trapped, which reduces stress.",
          },
          {
            id: "rural-hand-ground",
            type: "micro",
            title: "Touch the Land",
            description:
              "Gently touch a tree, wall, or ground near you. Notice texture, temperature, and how long it’s existed before today.",
            icon: "🌍",
            why:
              "Connecting to something stable and older than you can give a sense of perspective and groundedness.",
          },
        ],
      },

      {
        name: "Connection in Small Places",
        items: [
          {
            id: "rural-neighbour-check",
            type: "micro",
            title: "Neighbour Check-In",
            description:
              "Once a week, wave, message, or say a quick ‘you good?’ to someone nearby. Micro-connection counts.",
            icon: "🏡",
            why:
              "Regular small touchpoints create a web of support over time, even if each one feels tiny.",
          },
          {
            id: "rural-community-reflection",
            type: "reflection",
            title: "You’re Not the Only One",
            prompts: [
              "What’s one thing you wish people understood about rural stress?",
              "Who else in your community might quietly feel the same way?",
            ],
            why:
              "Reflecting on shared experience reduces the belief that you’re the only one struggling, which is a major driver of shame.",
          },
        ],
      },

      {
        name: "On-the-Road Support",
        items: [
          {
            id: "rural-audio-road",
            type: "audio",
            title: "On-the-Road Reset",
            subtitle:
              "Use any of the Emotional First-Aid audios while parked safely before/after a long drive.",
            file: "/assets/audio/Audio 4 -M.m4a",
            why:
              "Transition rituals before or after driving help your nervous system switch between “on duty” and “off duty.”",
          },
        ],
      },
    ],
  },
 /*"navigating-layoff": {
  emoji: "🧭",
  title: "Navigating a Layoff",
  intro:
    "A steady guide to help you regulate, reflect, and move forward after job loss — without rushing the process.",

  sections: [
    
    
    
        {
            name: "Grounding & Reset",
            items: [
              {
                id: "layoff-reset-2min",
                type: "interactive",
                title: "2-Minute Reset",
                steps: [
                  "Sit comfortably and breathe slowly.",
                  "Inhale for 4… Exhale for 6.",
                  "Notice your feet on the floor.",
                  "Scan your body for tension, and soften it."
                ],
                why:
                  "Short grounding exercises calm your nervous system so your mind doesn’t stay stuck in panic or overwhelm."
              },
              {
                id: "layoff-54321-grounding",
                type: "interactive",
                title: "5-4-3-2-1 Grounding",
                steps: [
                  "Name 5 things you can see.",
                  "Notice 4 things you can touch.",
                  "Listen for 3 sounds.",
                  "Identify 2 smells or memories of scent.",
                  "Acknowledge 1 thing you’re grateful for."
                ],
                why:
                  "Engaging your senses redirects your attention to the present and signals safety to your brain."
              },
              {
                id: "layoff-box-breathing",
                type: "breathing",
                title: "Box Breathing",
                description:
                  "Inhale 4 • Hold 4 • Exhale 4 • Hold 4 • Repeat for 4 rounds.",
                why:
                  "Box breathing balances your nervous system and reduces fight-or-flight stress."
              }
            ]
          },
          {
            name: "Emotional Support",
            items: [
              {
                id: "layoff-normalizing",
                type: "edu",
                title: "Your Feelings Are Normal",
                body:
                  "Job loss hits identity and safety. Feeling shock, sadness, or uncertainty is a human response — not a failure.",
                why:
                  "Understanding this reduces self-blame and creates space for regulation rather than judgment."
              },
              {
                id: "layoff-naming-loss",
                type: "reflection",
                title: "Name the Loss",
                prompts: [
                  "What part of this hurts the most right now?",
                  "What feels uncertain versus what is actually a fact?",
                  "What would you say to a friend in the same situation?"
                ],
                why:
                  "Putting feelings into words reduces emotional intensity and gives your brain clarity."
              }
            ]
          },
          {
            name: "Gentle Next Steps",
            items: [
              {
                id: "layoff-small-win",
                type: "micro",
                title: "Choose One Small Win",
                description:
                  "Pick one low-effort activity today — not job searching. Momentum grows from small signals of success.",
                why:
                  "Tiny wins help regulate mood and provide a sense of agency without overload."
              },
              {
                id: "layoff-organize-docs",
                type: "education",
                title: "Keep Paperwork Together",
                body:
                  "Save termination emails, pay stubs, contracts, and important dates in one place.",
                why:
                  "Reducing unknowns (where things are, what steps to take next) lowers stress."
              }
            ]
          },{
            name: "Deep Dive & Resources",
            items: [
              {
                id: "layoff-full-guide",
                type: "external",
                title: "Read the Full Navigating a Layoff Guide",
                description:
                  "A comprehensive article with practical steps, emotional validation, and pacing.",
                url: "https://helprr.ai/navigating-layoff"
              }
            ]
          },
        {
            id: "layoff-full-guide",
            type: "external",
            title: "Read the Full Navigating a Layoff Guide",
            description:
              "A longer, structured guide with practical steps, validation, and recovery pacing. Use this when you want depth beyond the toolkit.",
            url: "https://helprr.ai/navigating-layoff"
          }
          
      ]*/
     "navigating-layoff": {
  emoji: "🧭",
  title: "Navigating a Layoff",
  intro:
    "A steady guide to help you regulate, reflect, and move forward after job loss — without rushing the process.",
    fullGuide: {
        label: "Read full guide",
        url: "https://helprr.ai/navigating-layoff"
      },
  sections: [
    {
      name: "Grounding & Reset",
      items: [
        {
          id: "layoff-reset-2min",
          type: "interactive",
          title: "2-Minute Reset",
          steps: [
            "Sit comfortably and breathe slowly.",
            "Inhale for 4… Exhale for 6.",
            "Notice your feet on the floor.",
            "Scan your body for tension and soften it."
          ],
          why:
            "Grounding calms your nervous system so your mind doesn’t stay stuck in panic."
        },
        {
          id: "layoff-54321",
          type: "interactive",
          title: "5-4-3-2-1 Grounding",
          steps: [
            "Name 5 things you can see.",
            "Notice 4 things you can touch.",
            "Listen for 3 sounds.",
            "Identify 2 smells.",
            "Acknowledge 1 thing you’re grateful for."
          ],
          why:
            "Sensory grounding pulls your attention out of spirals and into safety."
        },
        {
          id: "layoff-breathing",
          type: "breathing",
          title: "Box Breathing",
          description:
            "Inhale 4 • Hold 4 • Exhale 4 • Hold 4 • Repeat for 4 rounds.",
          why:
            "Balanced breathing reduces fight-or-flight activation."
        }
      ]
    },

    {
      name: "Emotional Processing",
      items: [
        {
          id: "layoff-why-hard",
          type: "edu",
          title: "Why Layoffs Hit So Hard",
          body:
            "Job loss triggers threat responses tied to identity, safety, and belonging. Your nervous system reads it as danger — not failure.",
          why:
            "Understanding this reduces self-blame and confusion."
        },
        {
          id: "layoff-naming-loss",
          type: "reflection",
          title: "Name the Loss",
          prompts: [
            "What part of this hurts the most right now?",
            "What feels uncertain vs. what is actually known?",
            "What would you say to a friend in the same situation?"
          ],
          why:
            "Naming emotions lowers their intensity and restores clarity."
        }
      ]
    },

    {
      name: "Gentle Forward Motion",
      items: [
        {
          id: "layoff-small-win",
          type: "micro",
          title: "One Small Win",
          description:
            "Choose one low-effort action today — not job searching.",
          why:
            "Tiny wins restore agency without overwhelm."
        },
        {
          id: "layoff-dont-rush",
          type: "edu",
          title: "Why You Don’t Need to Rush",
          body:
            "Pushing immediately into productivity can delay emotional recovery. Stabilization first leads to better long-term outcomes.",
          why:
            "Regulation before action leads to better decisions."
        }
      ]
    },

    {
      name: "Go Deeper",
      items: [
        {
          id: "layoff-full-guide-link",
          type: "micro",
          title: "Read the Full Navigating a Layoff Guide",
          description:
            "A longer, structured guide with validation, pacing, and practical steps.",
          why:
            "Use this when you want depth beyond the toolkit.",
          link: "https://helprr.ai/navigating-layoff"
        }
      ]
    }
  ]
}

    }

  /*  {
        
      name: "Emotional Processing",
      items: [
        { id: "reflection-exercise",
          type: "reflection",
          title: "Naming the Loss",
          prompts: [
            "What part of this hurts the most right now?",
            "What feels uncertain — and what is actually known?",
            "What would you say to a friend in the same situation?"
          ]
        },
        {
            id: "audio-message",
          type: "audio",
          title: "You Are Not Behind",
          file: "/assets/audio/Capsule_You_Are_Not_Behind.m4a"
        }
      ]
    },

    {
      name: "Gentle Forward Motion",
      items: [
        {
            id: "small-win",
          type: "micro",
          title: "One Small Win",
          description:
            "Choose one low-effort action today — not job searching. Momentum comes later."
        },
        {id: "stablize-exercise",
          type: "education",
          title: "Why You Don’t Need to Rush",
          content:
            "Pushing immediately into productivity can delay emotional recovery. Stabilization first leads to better long-term outcomes."
        }
      ]
    }
  ]
}

};*/

// -----------------------------
// SMALL UI COMPONENTS
// -----------------------------

function WhyBlock({ text }) {
  if (!text) return null;
  return (
    <div
      style={{
        marginTop: 10,
        padding: "10px 12px",
        borderRadius: 10,
        background: "#fff7f0",
        border: "1px dashed #e1c3aa",
        fontSize: 13,
        color: "#50352a",
      }}
    >
      <strong>Why this helps:</strong> {text}
    </div>
  );
}

function EducationCard({ item, completed, onToggleComplete }) {
  return (
    <div
      className={
        "toolkit-card toolkit-card--reflection" +
        (completed ? " toolkit-card--done" : "")
      }
    >
      <div className="toolkit-card-header">
        <h3 className="toolkit-card__title">{item.title}</h3>
        <button className="toolkit-check-btn" onClick={onToggleComplete}>
          {completed ? "✔ Read" : "Mark read"}
        </button>
      </div>
      <p className="toolkit-subtitle" style={{ marginBottom: 8 }}>
        {item.body}
      </p>

      {item.miniLessonTitle && (
        <p
          style={{
            marginTop: 10,
            fontSize: 13,
            fontWeight: 600,
            color: "#4b3c5d",
          }}
        >
          {item.miniLessonTitle}
        </p>
      )}
      {item.miniLessonBody && (
        <p style={{ fontSize: 13, color: "#554c66" }}>{item.miniLessonBody}</p>
      )}

      {item.links && item.links.length > 0 && (
        <div
          style={{
            marginTop: 10,
            paddingTop: 8,
            borderTop: "1px solid rgba(0,0,0,0.05)",
            fontSize: 12,
            color: "#555",
          }}
        >
          <strong>Try next:</strong>
          <ul style={{ paddingLeft: 18, marginTop: 4, marginBottom: 0 }}>
            {item.links.map((l, idx) => (
              <li key={idx}>{l}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function InteractiveStepsCard({ item, completed, onToggleComplete }) {
  const [stepIndex, setStepIndex] = useState(0);
  const total = item.steps.length;
  const isLast = stepIndex === total - 1;

  const next = () => {
    if (!isLast) setStepIndex((s) => s + 1);
  };

  const resetSteps = () => setStepIndex(0);

  const handleDone = () => {
    onToggleComplete();
  };

  return (
    <div
      className={
        "toolkit-card toolkit-card--interactive" +
        (completed ? " toolkit-card--done" : "")
      }
    >
      <div className="toolkit-card-header">
        <h3 className="toolkit-card__title">{item.title}</h3>
        <button className="toolkit-check-btn" onClick={handleDone}>
          {completed ? "✔ Done" : "Mark done"}
        </button>
      </div>

      <div className="toolkit-steps-header">
        <span className="toolkit-steps-pill">
          Step {stepIndex + 1} of {total}
        </span>
      </div>

      <p className="toolkit-steps-text">{item.steps[stepIndex]}</p>

      <div className="toolkit-steps-dots">
        {item.steps.map((_, idx) => (
          <span
            key={idx}
            className={
              idx === stepIndex
                ? "toolkit-dot toolkit-dot--active"
                : "toolkit-dot"
            }
          />
        ))}
      </div>

      <div className="toolkit-steps-actions">
        {!isLast ? (
          <button className="toolkit-btn" onClick={next}>
            Next step →
          </button>
        ) : (
          <button className="toolkit-btn-secondary" onClick={resetSteps}>
            Restart steps ↺
          </button>
        )}
      </div>

      <WhyBlock text={item.why} />
    </div>
  );
}

function ReflectionCard({ item, completed, onToggleComplete }) {
  return (
    <div
      className={
        "toolkit-card toolkit-card--reflection" +
        (completed ? " toolkit-card--done" : "")
      }
    >
      <div className="toolkit-card-header">
        <h3 className="toolkit-card__title">{item.title}</h3>
        <button className="toolkit-check-btn" onClick={onToggleComplete}>
          {completed ? "✔ Done" : "Mark done"}
        </button>
      </div>
      <p className="toolkit-subtitle">
        Use these prompts as gentle conversation with yourself.
      </p>
      <div className="toolkit-prompts">
        {item.prompts.map((p, idx) => (
          <p key={idx} className="toolkit-prompt">
            {p}
          </p>
        ))}
      </div>
      <WhyBlock text={item.why} />
    </div>
  );
}

function BreathingCard({ item, completed, onToggleComplete }) {
  const [playing, setPlaying] = useState(false);

  const toggle = () => setPlaying((p) => !p);

  return (
    <div
      className={"toolkit-card" + (completed ? " toolkit-card--done" : "")}
    >
      <div className="toolkit-card-header">
        <h3 className="toolkit-card__title">{item.title}</h3>
        <button className="toolkit-check-btn" onClick={onToggleComplete}>
          {completed ? "✔ Done" : "Mark done"}
        </button>
      </div>

      <p className="toolkit-subtitle">{item.description}</p>

      <div className="toolkit-breathing-wrapper">
        <div
          className={
            playing
              ? "breathing-circle breathing-circle--active"
              : "breathing-circle"
          }
        />
        <p className="toolkit-breathing-text">
          Inhale as the circle expands • Exhale as it softens.
        </p>
        <button className="toolkit-btn-secondary" onClick={toggle}>
          {playing ? "Pause animation" : "Start animation"}
        </button>
      </div>

      {item.file && (
        <audio
          controls
          style={{ width: "100%", marginTop: 12 }}
          src={item.file}
        />
      )}

      <WhyBlock text={item.why} />
    </div>
  );
}

function MicroCard({ item, completed, onToggleComplete }) {
    return (
      <div
        className={
          "toolkit-micro-card" +
          (completed ? " toolkit-micro-card--done" : "")
        }
      >
        <div className="toolkit-micro-icon">{item.icon || "🌱"}</div>
  
        <div className="toolkit-micro-main">
          <div className="toolkit-micro-header">
            <h4 className="toolkit-micro-title">{item.title}</h4>
  
            {onToggleComplete && (
              <button
                className="toolkit-check-btn-small"
                onClick={onToggleComplete}
              >
                {completed ? "✔" : "○"}
              </button>
            )}
            
          </div>
  
          <p className="toolkit-micro-text">{item.description}</p>
  
          {/* 👉 LINK CTA */}
          {item.link && (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="toolkit-link-cta"
            >
              Open full guide →
            </a>
          )}
  
          <WhyBlock text={item.why} />
        </div>
      </div>
    );
  }
  


function AudioCard({ item, completed, onToggleComplete }) {
  return (
    <div
      className={"toolkit-card" + (completed ? " toolkit-card--done" : "")}
    >
      <div className="toolkit-card-header">
        <h3 className="toolkit-card__title">{item.title}</h3>
        <button className="toolkit-check-btn" onClick={onToggleComplete}>
          {completed ? "✔ Done" : "Mark done"}
        </button>
      </div>
      {item.subtitle && (
        <p className="toolkit-subtitle">{item.subtitle}</p>
      )}
      {item.file && (
        <audio
          controls
          style={{ width: "100%", marginTop: 12 }}
          src={item.file}
        />
      )}
      <WhyBlock text={item.why} />
    </div>
  );
} function ExternalResourceCard({ item }) {
    return (
      <div className="toolkit-card toolkit-card--external">
        <h3 className="toolkit-card__title">{item.title}</h3>
        <p className="toolkit-subtitle">{item.description}</p>
  
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="toolkit-external-link"
        >
          Open full guide →
        </a>
      </div>
    );
  }
  

// -----------------------------
// MAIN COMPONENT
// -----------------------------
export default function ToolkitDetail() {
  const { slug } = useParams();
  const isRuralWellness = slug === "rural-wellness";
  const data = TOOLKIT_DATA[slug];

  // theme state (warm / calm / night)
  const [theme, setTheme] = useState(
    localStorage.getItem("helprr_theme") || "warm"
  );

//   useEffect(() => {
//     document.body.setAttribute("data-helprr-theme", theme);
//     localStorage.setItem("helprr_theme", theme);
//   }, [theme]);

  const [progressMap, setProgressMap] = useState({});

  // all completable item IDs
  const completableIds = useMemo(() => {
    if (!data) return [];
    return data.sections.flatMap((section) =>
      section.items.filter((item) => item.id).map((item) => item.id)
    );
  }, [data]);

  // Load from localStorage
  useEffect(() => {
    // if (!slug) return;
    if (!slug || isRuralWellness) return;
    const stored = localStorage.getItem(`helprr_toolkit_progress_${slug}`);
    if (stored) {
      try {
        setProgressMap(JSON.parse(stored));
      } catch {
        setProgressMap({});
      }
    } else {
      setProgressMap({});
    }
  }, [slug, isRuralWellness]);

  useEffect(() => {
    if (!slug || isRuralWellness) return;

    localStorage.setItem(
      `helprr_toolkit_progress_${slug}`,
      JSON.stringify(progressMap)
    );
  }, [slug, progressMap, isRuralWellness]);

  // Save to localStorage
//   useEffect(() => {
//     if (!slug) return;
//     localStorage.setItem(
//       `helprr_toolkit_progress_${slug}`,
//       JSON.stringify(progressMap)
//     );
//   }, [slug, progressMap]);

  const completedCount = completableIds.filter((id) => progressMap[id]).length;
  const totalCompletable = completableIds.length;
  const progress = totalCompletable
    ? Math.round((completedCount / totalCompletable) * 100)
    : 0;

  // Confetti when 100%
  useEffect(() => {
    if (
      progress === 100 &&
      typeof window !== "undefined" &&
      window.confetti
    ) {
      const duration = 1800;
      const end = Date.now() + duration;

      (function frame() {
        window.confetti({
          particleCount: 3,
          startVelocity: 25,
          spread: 60,
          ticks: 50,
          origin: { x: Math.random(), y: Math.random() - 0.2 },
        });

        if (Date.now() < end) requestAnimationFrame(frame);
      })();
    }
  }, [progress]);

  if (!data) return <div>Not found</div>;

  const toggleComplete = (id) => {
    if (!id) return;
    setProgressMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };
  if (slug === "rural-wellness") {
    return <RuralWellnessToolkit />;
  }
  

  return (
    <>
      <OuterHeader />

      {/* THEME SWITCHER */}
      {/* <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          maxWidth: 900,
          margin: "10px auto 0",
          padding: "0 20px",
        }}
      >
        <select
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
          style={{
            padding: "8px 12px",
            borderRadius: "10px",
            border: "1px solid #e0c2b8",
            background: "white",
            cursor: "pointer",
            fontSize: 14,
          }}
        >
          <option value="warm">🌤️ Warm Mode</option>
          <option value="calm">🌙 Calm Mode</option>
          <option value="night">🌑 Night Mode</option>
        </select>
      </div> */}

      {/* HERO */}
      <div className="toolkit-hero">
      {/* <Link to="/toolkits" className="toolkit-back">
          ← Back
        </Link> */}

        <h1 style={{ fontSize: 34, marginTop: 15, color: "#F16A37"  }}>
          {data.emoji} {data.title}
        </h1>
{/* HERO CTA */}
{data.fullGuide && (
  <div style={{ marginTop: 18 }}>
    <a
      href={data.fullGuide.url}
      target="_blank"
      rel="noopener noreferrer"
      className="toolkit-hero-cta"
    >
      📘 {data.fullGuide.label} →
    </a>
  </div>
)}

        <p style={{ fontSize: 18, marginTop: 10, color: "#555" }}>
          {data.intro}
        </p>

        {/* Progress */}
        {totalCompletable > 0 && (
          <div className="toolkit-progress-wrapper">
            <div className="toolkit-progress-top">
              <span className="toolkit-progress-label">
                Your progress in this toolkit
              </span>
              <span className="toolkit-progress-count">
                {completedCount}/{totalCompletable} done ({progress}%)
              </span>
            </div>
            <div className="toolkit-progress-bar">
              <div
                className={
                  "toolkit-progress-fill" +
                  (progress === 100 ? " full-animation" : "")
                }
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* SECTIONS */}
      <div className="toolkit-container">
        {data.sections.map((section, idx) => (
          <div key={idx} className="toolkit-section">
            <div className="toolkit-section-header">
              <span>{section.name}</span>
            </div>

            <div className="toolkit-section-body">
              {section.items.map((item, i) => {
                const completed = item.id && progressMap[item.id];

                const commonProps = {
                  item,
                  completed: !!completed,
                  onToggleComplete: () => toggleComplete(item.id),
                };

                if (item.type === "edu") {
                  return <EducationCard key={i} {...commonProps} />;
                }
                if (item.type === "interactive") {
                  return <InteractiveStepsCard key={i} {...commonProps} />;
                }
                if (item.type === "reflection") {
                  return <ReflectionCard key={i} {...commonProps} />;
                }
                if (item.type === "breathing") {
                  return <BreathingCard key={i} {...commonProps} />;
                }
                if (item.type === "micro") {
                  return <MicroCard key={i} {...commonProps} />;
                }if (item.type === "external") {
                    return <ExternalResourceCard key={i} item={item} />;
                  }
                  
                return <AudioCard key={i} {...commonProps} />;
              })}
            </div>
          </div>
        ))}
      </div>

      <Footer />
    </>
  );
}
